import database from '_database-special-case';

import { type CacheDocumentLocation, type DocumentContent, type OngoingOperationsByLocation, DocumentLocation, LoadingStrategy } from '../../types';
import { AbortablePromise } from '../../utils/AbortablePromise';
import nowTimestamp from '../../utils/dates/nowTimestamp';
import makeLogger, { type ReadwiseLogger } from '../../utils/makeLogger';
import type { KeysOfType } from '../../utils/typescriptUtils';
// eslint-disable-next-line import/no-cycle
import { purgeAudiobooksForDocuments } from '../audiobookTrackLoading';
// eslint-disable-next-line import/no-cycle
import * as documentChunks from '../documentChunkLoading';
// eslint-disable-next-line import/no-cycle
import foreground from '../portalGates/toForeground';
import CacheBackedStore from './CacheBackedStore';
import type { ItemStoreConstructorArgs } from './types';

const PRIORITY_ORDERED_LOCATIONS: ReadonlyArray<CacheDocumentLocation> = [
  DocumentLocation.New,
  DocumentLocation.Later,
  DocumentLocation.Shortlist,
  DocumentLocation.Archive,
  DocumentLocation.Feed,
] as const;

type OngoingLocationFullSync = {
  nextCursor: string;
  requestStartDatetime: number;
};

/**
 * Manages offline document content synchronization with support for two strategies
 * during the transition period:
 *
 * - Global (deprecated): Original strategy that syncs all documents regardless of location.
 *   Still supported for backwards compatibility during migration.
 * - Location-based: New strategy that syncs documents per location (New, Later, Shortlist,
 *   Archive, Feed), allowing users to selectively download content based on their reading
 *   lists to optimize storage and bandwidth.
 *
 * Location-based syncing:
 * - Each location maintains its own sync state (cursor, timestamp)
 * - Locations can be individually enabled/disabled
 * - Syncs run sequentially by priority (New → Later → Shortlist → Archive → Feed)
 * - Disabled locations have their content purged from cache
 * - Incremental updates use timestamps to fetch only changed documents
 *
 * Syncing for each location adds a lot of new complexity that is unique to
 * document content loading. Thus, we extend the `CacheBackedStore` that should provide a
 * generic interface for downloading any kind of content from the backend. This store specifically
 * handles the lifecyle of document content.
 *
 * @extends {CacheBackedStore<TItem, TIdPropertyName>}
 */
class DocumentContentStore<TItem, TIdPropertyName extends KeysOfType<TItem, string>>
  extends CacheBackedStore<TItem, TIdPropertyName> {

  private readonly _ongoingLocationPromises = new Map<string, AbortablePromise<void>>();
  private _documentLogger: ReadwiseLogger;

  constructor(options: ItemStoreConstructorArgs) {
    super(options);

    this._documentLogger = makeLogger(`${options.name} document store`);
  }

  public async initializeLoading(options?: { isInitialLoad?: boolean; }): Promise<void> {
    this._documentLogger.info('initializeLoading started', { isInitialLoad: options?.isInitialLoad });

    const experimentalOfflineEnabled = await foreground.getExperimentalOfflineLoadingEnabled();

    if (!options?.isInitialLoad) {
      await this.abortAllOperations();
    }

    const targetStrategy = experimentalOfflineEnabled
      ? LoadingStrategy.Location
      : LoadingStrategy.Global;

    this._documentLogger.info('Loading strategy determined', {
      targetStrategy,
      experimentalOfflineEnabled,
    });

    await this.migrateLoadingStrategy(targetStrategy);

    if (targetStrategy === LoadingStrategy.Location) {
      // NOTE: We currently don't purge disabled locations to avoid potential performance issues.
      // Keeping offline content cached even when locations are disabled allows users to still
      // access previously downloaded content and avoids unnecessary re-downloads if they re-enable.
      // if (!options?.isInitialLoad) {
      //   const disabledLocations = this.getDisabledLocations(locationPrefs);
      //   for (const location of disabledLocations) {
      //     await this.purgeLocation(location);
      //   }
      // }

      await this.resumeAllEnabledLocations();
    } else {
      try {
        await this.loadAll();
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError' && !error.message.includes('abort')) {
          this._documentLogger.error('Global loadAll failed unexpectedly', { error });
          throw error;
        }
      }
    }

    await this.updateForegroundDownloadingLocation();

    this._documentLogger.info('initializeLoading completed', { targetStrategy });
  }

  public getOngoingOperations(): OngoingOperationsByLocation {
    const ongoingOps: OngoingOperationsByLocation = {};
    // Explanation: _ongoingLocationPromises is keyed by location strings; the Map key type is string but
    // all keys are CacheDocumentLocation values by construction.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const keys = Array.from(this._ongoingLocationPromises.keys()) as CacheDocumentLocation[];
    for (const location of keys) {
      ongoingOps[location] = true;
    }
    return ongoingOps;
  }

  public async isLocationSyncInitial(location: CacheDocumentLocation): Promise<boolean> {
    const lastLocationSync = await this._cache.getItem<number>(`locationLastSync_${location}`);
    return !lastLocationSync;
  }

  public async setLocationEnabled(location: CacheDocumentLocation, enabled: boolean): Promise<void> {
    this._documentLogger.info('setLocationEnabled', { location, enabled });

    await this.cancelLocationOperation(location);

    if (enabled) {
      try {
        await this.loadForLocation(location);
        this._documentLogger.info('setLocationEnabled completed', { location });
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        if (errorMessage.includes('aborted') || e instanceof Error && e.name === 'AbortError') {
          this._documentLogger.debug('setLocationEnabled cancelled', { location });
        } else {
          this._documentLogger.error('setLocationEnabled failed', { location, errorMessage, e });
          throw e;
        }
      }
    } else {
      // NOTE: We currently don't purge disabled locations to avoid potential performance issues.
      // Keeping offline content cached even when locations are disabled allows users to still
      // access previously downloaded content and avoids unnecessary re-downloads if they re-enable.
      // await this.purgeLocation(location);
    }
  }

  public async purgeByIds(...id: string[]): Promise<void> {
    const docsToPurge = await database.collections.documents.find({
      selector: {
        parsed_doc_id: { $in: id.map((id) => parseInt(id, 10)) },
      },
    });
    this._documentLogger.debug('purgeByIds: docs to purge', { docsToPurge: docsToPurge.length });

    const docIds = [];
    const parsedDocIds: string[] = [];
    for (const doc of docsToPurge) {
      if (doc.id) {
        docIds.push(doc.id);
      }
      if (Number.isFinite(doc.parsed_doc_id)) {
        parsedDocIds.push(String(doc.parsed_doc_id));
      }
    }
    this._documentLogger.debug('purgeByIds: parsed doc ids to cleanup', { parsedDocIds: parsedDocIds.length });

    const chunkIdsToRemove = new Set<string>();
    for (const doc of docsToPurge) {
      const epubManifest = await foreground.getChunkManifestForDocument(doc.id);
      if (!epubManifest) {
        continue;
      }
      for (const entry of Object.values(epubManifest)) {
        if (entry?.id) {
          chunkIdsToRemove.add(entry.id);
        }
      }
    }
    this._documentLogger.debug('purgeByIds: chunk ids to remove', { chunkIdsToRemove: chunkIdsToRemove.size });

    // Explanation: getItemsByIds returns { [id: string]: TItem } where TItem is DocumentContent;
    // the cast adds `| undefined` to accurately model potential cache misses.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const cachedContent = await this.getItemsByIds(parsedDocIds) as {
      [parsedDocId: string]: DocumentContent | undefined;
    };
    await purgeAudiobooksForDocuments(
      docsToPurge,
      'unknown',
      cachedContent,
      false,
    );
    this._documentLogger.debug('purgeByIds: purged audiobooks for documents');

    await this.removeByIds(parsedDocIds);
    this._documentLogger.debug('purgeByIds: removed parsed docs');

    await documentChunks.removeQueuedManifestsForDocIds(docIds);
    this._documentLogger.debug('purgeByIds: removed enqueued manifests for doc ids');

    if (chunkIdsToRemove.size > 0) {
      await documentChunks.removeChunksByIds(Array.from(chunkIdsToRemove));
      this._documentLogger.debug('purgeByIds: removed chunk ids');
    }
  }

  private async cancelLocationOperation(location: CacheDocumentLocation): Promise<void> {
    const existingPromise = this._ongoingLocationPromises.get(location);
    if (existingPromise) {
      this._documentLogger.debug('Canceling ongoing location operation', {
        location,
        timestamp: nowTimestamp(),
      });
      existingPromise.abort();
      try {
        await existingPromise;
        this._documentLogger.debug('Successfully canceled location operation', {
          location,
          timestamp: nowTimestamp(),
        });
      } catch (error) {
        this._documentLogger.debug('Location operation canceled (expected)', {
          location,
          error: error instanceof Error ? error.message : String(error),
          timestamp: nowTimestamp(),
        });
      }
      this._ongoingLocationPromises.delete(location);
    } else {
      this._documentLogger.debug('No ongoing location operation to cancel', {
        location,
        timestamp: nowTimestamp(),
      });
    }
  }

  private async clearLocationSyncState(location: CacheDocumentLocation): Promise<void> {
    await this.cancelLocationOperation(location);
    await Promise.all([
      this._cache.removeItem(`ongoingLocationFullSync_${location}`),
      this._cache.removeItem(`ongoingLocationUpdatesCursor_${location}`),
      this._cache.removeItem(`locationLastSync_${location}`),
    ]);
    this._documentLogger.debug('Cleared sync state for location', { location });
  }

  private async abortAllOperations(): Promise<void> {
    this._documentLogger.debug('Aborting all operations');

    await this.abortLoadAll();

    const locationAborts = Array.from(this._ongoingLocationPromises.entries(), async ([location, promise]) => {
      this._documentLogger.debug('Aborting location operation', { location });
      promise.abort();
      try {
        await promise;
      } catch {
        this._documentLogger.debug('Location operation aborted', { location });
      }
    });

    await Promise.all(locationAborts);
    this._ongoingLocationPromises.clear();

    this._documentLogger.debug('All operations aborted');
  }

  private async clearAllLocationSyncStates(): Promise<void> {
    const locations: CacheDocumentLocation[] = [
      DocumentLocation.New,
      DocumentLocation.Later,
      DocumentLocation.Shortlist,
      DocumentLocation.Archive,
      DocumentLocation.Feed,
    ];

    this._documentLogger.debug('Clearing all location sync states');
    await Promise.all(
      locations.map((location) => this.clearLocationSyncState(location)),
    );
    this._documentLogger.debug('All location sync states cleared');
  }

  private async detectCurrentLoadingStrategy(): Promise<LoadingStrategy | null> {
    // We are checking the set sync flags for determining which loading strategy
    // this device was on before switching to the new loading strategy.
    const lastLoadAllDatetime = await this._cache.getItem('lastLoadAllDatetime');
    const ongoingFullLoadAll = await this._cache.getItem('ongoingFullLoadAll');
    const ongoingLoadUpdatesCursor = await this._cache.getItem('ongoingLoadUpdatesCursor');

    const hasGlobalState = lastLoadAllDatetime || ongoingFullLoadAll || ongoingLoadUpdatesCursor;

    const locations: CacheDocumentLocation[] = [
      DocumentLocation.New,
      DocumentLocation.Later,
      DocumentLocation.Shortlist,
      DocumentLocation.Archive,
      DocumentLocation.Feed,
    ];

    let hasLocationState = false;
    const locationStates: { [key: string]: { hasState: boolean; details: { [key: string]: boolean; }; }; } = {};

    for (const location of locations) {
      const locationLastSync = await this._cache.getItem(`locationLastSync_${location}`);
      const ongoingLocationFullSync = await this._cache.getItem(`ongoingLocationFullSync_${location}`);
      const ongoingLocationUpdatesCursor = await this._cache.getItem(`ongoingLocationUpdatesCursor_${location}`);

      const locationHasState = locationLastSync || ongoingLocationFullSync || ongoingLocationUpdatesCursor;

      locationStates[location] = {
        hasState: Boolean(locationHasState),
        details: {
          locationLastSync: Boolean(locationLastSync),
          ongoingLocationFullSync: Boolean(ongoingLocationFullSync),
          ongoingLocationUpdatesCursor: Boolean(ongoingLocationUpdatesCursor),
        },
      };

      if (locationHasState) {
        hasLocationState = true;
      }
    }

    let detectedStrategy: LoadingStrategy | null = null;

    if (hasGlobalState && !hasLocationState) {
      detectedStrategy = LoadingStrategy.Global;
    } else if (hasLocationState && !hasGlobalState) {
      detectedStrategy = LoadingStrategy.Location;
    } else if (hasGlobalState && hasLocationState) {
      detectedStrategy = LoadingStrategy.Location;
      this._documentLogger.warn('Both global and location sync states exist - preferring LOCATION strategy', {
        globalState: { lastLoadAllDatetime: Boolean(lastLoadAllDatetime), ongoingFullLoadAll: Boolean(ongoingFullLoadAll), ongoingLoadUpdatesCursor: Boolean(ongoingLoadUpdatesCursor) },
        locationState: locationStates,
      });
    } else {
      detectedStrategy = null;
      this._documentLogger.info('No existing sync state detected - fresh start');
    }

    return detectedStrategy;
  }

  private async migrateLoadingStrategy(targetStrategy: LoadingStrategy): Promise<void> {
    this._documentLogger.info('Starting loading strategy migration check', { targetStrategy });

    const currentStrategy = await this.detectCurrentLoadingStrategy();

    if (!currentStrategy || currentStrategy === targetStrategy) {
      this._documentLogger.info('No migration needed', {
        currentStrategy: currentStrategy ?? 'none',
        targetStrategy,
        reason: !currentStrategy ? 'fresh start' : 'already using target strategy',
      });
      return;
    }

    this._documentLogger.info('Starting loading strategy migration', {
      from: currentStrategy,
      to: targetStrategy,
    });

    await this.abortAllOperations();

    if (currentStrategy === LoadingStrategy.Global && targetStrategy === LoadingStrategy.Location) {
      const lastGlobalSync = await this._cache.getItem<number>('lastLoadAllDatetime');

      await this.clearGlobalSyncState();

      if (lastGlobalSync) {
        const locations: CacheDocumentLocation[] = [
          DocumentLocation.New,
          DocumentLocation.Later,
          DocumentLocation.Shortlist,
          DocumentLocation.Archive,
          DocumentLocation.Feed,
        ];

        // Set all locations to use global timestamp for incremental updates
        const locationUpdates = locations.map((location) =>
          this._cache.setItem(`locationLastSync_${location}`, lastGlobalSync));
        await Promise.all(locationUpdates);

        this._documentLogger.info('Migration optimization applied: preserved global sync timestamp for locations', {
          globalTimestamp: lastGlobalSync,
          locationsUpdated: locations.length,
        });
      } else {
        this._documentLogger.info('Migration will require full sync: no global timestamp available');
      }
    } else if (currentStrategy === LoadingStrategy.Location && targetStrategy === LoadingStrategy.Global) {
      // Use oldest location timestamp if all locations have completed syncs
      const offlinePrefs = await foreground.getOfflineCacheEnabledByLocation();
      const enabledLocations = Object.entries(offlinePrefs)
        .filter(([, isEnabled]) => isEnabled)
        // Explanation: Object.entries keys are string, but offlinePrefs is { [key in CacheDocumentLocation]?: boolean }
        // so all keys are CacheDocumentLocation values at runtime.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .map(([location]) => location as CacheDocumentLocation);

      const locationTimestamps = await Promise.all(
        enabledLocations.map((location) =>
          this._cache.getItem<number>(`locationLastSync_${location}`)),
      );

      const validTimestamps = locationTimestamps.filter((timestamp): timestamp is number => Boolean(timestamp));
      const completedCount = validTimestamps.length;
      const oldestSync = validTimestamps.length > 0 ? Math.min(...validTimestamps) : null;

      const canOptimize = enabledLocations.length > 0 && completedCount === enabledLocations.length;

      await this.clearAllLocationSyncStates();

      if (canOptimize && oldestSync) {
        await this._cache.setItem('lastLoadAllDatetime', oldestSync);

        this._documentLogger.info('Migration optimization applied: preserved oldest location sync timestamp for global', {
          oldestTimestamp: oldestSync,
          enabledLocations: enabledLocations.length,
          completedLocations: completedCount,
        });
      } else {
        this._documentLogger.info('Migration will require full sync', {
          reason: !canOptimize ? 'not all enabled locations completed sync' : 'no location timestamps available',
          enabledLocations: enabledLocations.length,
          completedLocations: completedCount,
        });
      }
    } else {
      this._documentLogger.warn('Unexpected migration path', { currentStrategy, targetStrategy });
    }

    this._documentLogger.info('Loading strategy migration completed successfully', {
      from: currentStrategy,
      to: targetStrategy,
    });
  }

  private async resumeAllEnabledLocations(): Promise<void> {
    this._documentLogger.debug('resumeAllEnabledLocations: starting');

    try {
      const offlinePrefs = await foreground.getOfflineCacheEnabledByLocation();
      const enabledLocations = Object.entries(offlinePrefs)
        .filter(([, isEnabled]) => isEnabled)
        .map(([location]) => location);

      this._documentLogger.debug('resumeAllEnabledLocations: enabled locations', {
        offlinePrefs,
        enabledLocations,
      });

      if (enabledLocations.length === 0) {
        this._documentLogger.debug('resumeAllEnabledLocations: no enabled locations, skipping');
        return;
      }

      const orderedEnabledLocations = PRIORITY_ORDERED_LOCATIONS.filter((location) =>
        enabledLocations.includes(location));

      let processedCount = 0;
      for (const location of orderedEnabledLocations) {
        try {
          this._documentLogger.debug('resumeAllEnabledLocations: processing location', {
            location,
            position: processedCount + 1,
            total: orderedEnabledLocations.length,
          });
          await this.resumeLocationIfNeeded(location);
          processedCount++;
        } catch (error) {
          this._documentLogger.warn('Failed to resume location', { location, error });
        }
      }
      this._documentLogger.info('resumeAllEnabledLocations: completed', {
        processedLocations: processedCount,
        totalEnabled: enabledLocations.length,
      });
    } catch (error) {
      this._documentLogger.error('resumeAllEnabledLocations: failed', { error });
    }
  }

  private async clearGlobalSyncState(): Promise<void> {
    await Promise.all([
      this._cache.removeItem('lastLoadAllDatetime'),
      this._cache.removeItem('ongoingFullLoadAll'),
      this._cache.removeItem('ongoingLoadUpdatesCursor'),
    ]);
    this._documentLogger.debug('Cleared global sync state');
  }

  private loadForLocation(location: CacheDocumentLocation): AbortablePromise<void> {
    this._documentLogger.debug('loadForLocation called', { location });

    return AbortablePromise.create<void>(async (resolve, reject, signal) => {
      try {
        const offlinePrefs = await foreground.getOfflineCacheEnabledByLocation();
        if (!offlinePrefs[location]) {
          this._documentLogger.warn('Attempted to load disabled location', { location });
          // NOTE: We don't clear sync state for disabled locations to preserve the last sync timestamp.
          // This allows for efficient incremental syncing when the location is re-enabled.
          // await this.clearLocationSyncState(location);
          resolve();
          return;
        }

        if (this._ongoingLocationPromises.has(location)) {
          this._documentLogger.debug('Location already loading, skipping', { location });
          resolve();
          return;
        }

        const lastLocationSync = await this._cache.getItem<number>(`locationLastSync_${location}`);
        const isInitialSync = !lastLocationSync;

        const abortablePromise = AbortablePromise.create<void>((innerResolve, innerReject, innerSignal) => {
          this.loadLocationWithCursor(location, innerSignal)
            .then(() => innerResolve())
            .catch(innerReject);
        });

        this._ongoingLocationPromises.set(location, abortablePromise);

        this.emit('sync-started', { location, isInitialSync });

        if (isInitialSync) {
          await this.updateForegroundDownloadingLocation();
        }

        try {
          await abortablePromise;
          this._ongoingLocationPromises.delete(location);
          this.emit('sync-completed', { location });
          await this.updateForegroundDownloadingLocation();
          resolve();
        } catch (error) {
          this._ongoingLocationPromises.delete(location);
          this.emit('sync-completed', { location });
          await this.updateForegroundDownloadingLocation();
          reject(error);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  private async purgeLocation(location: CacheDocumentLocation): Promise<{
    removedParsedDocIds: string[];
    removedChunkIds: string[];
    removedAudiobookTrackIds: string[];
    removedQueuedDocIds: string[];
  }> {
    const startTime = performance.now();
    this._documentLogger.debug('purgeLocation: STARTING', {
      location,
      timestamp: nowTimestamp(),
    });

    this.emit('purge-started', { location });

    const clearSyncStateStart = performance.now();
    await this.clearLocationSyncState(location);
    const clearSyncStateEnd = performance.now();
    this._documentLogger.debug('purgeLocation: clearLocationSyncState completed', {
      location,
      duration: clearSyncStateEnd - clearSyncStateStart,
    });

    try {
      const dbQueryStart = performance.now();
      const docsInLocation = await database.collections.documents.find({
        selector: {
          triage_status: location,
          parsed_doc_id: { $exists: true },
        },
      });
      const dbQueryEnd = performance.now();
      this._documentLogger.debug('purgeLocation: database query completed', {
        location,
        documentCount: docsInLocation.length,
        duration: dbQueryEnd - dbQueryStart,
      });

      const docProcessingStart = performance.now();
      const docIds: string[] = [];
      const parsedDocIds: string[] = [];

      for (const doc of docsInLocation) {
        if (doc.id) {
          docIds.push(doc.id);
        }
        if (Number.isFinite(doc.parsed_doc_id)) {
          parsedDocIds.push(String(doc.parsed_doc_id));
        }
      }
      const docProcessingEnd = performance.now();
      this._documentLogger.debug('purgeLocation: document ID processing completed', {
        location,
        docIds: docIds.length,
        parsedDocIds: parsedDocIds.length,
        duration: docProcessingEnd - docProcessingStart,
      });

      // OPTIMIZATION: Skip chunk processing for feed locations since feed documents are never chunked.
      // This avoids unnecessarily looping through potentially thousands of feed documents.
      // NOTE: This optimization works because we currently don't chunk any documents in the feed.
      // If we ever start chunking regular documents and allow them in the feed, this logic would
      // need to be removed or modified to check document types rather than location.
      const isFeedLocation = location === DocumentLocation.Feed;

      const manifestProcessingStart = performance.now();
      const chunkIdsToRemove = new Set<string>();

      if (!isFeedLocation) {
        for (const doc of docsInLocation) {
          const epubManifest = await foreground.getChunkManifestForDocument(doc.id);
          if (!epubManifest) {
            continue;
          }
          for (const entry of Object.values(epubManifest)) {
            if (entry.id) {
              chunkIdsToRemove.add(entry.id);
            }
          }
        }
      }

      const manifestProcessingEnd = performance.now();
      this._documentLogger.debug('purgeLocation: source_specific_data manifest processing completed', {
        location,
        chunkIds: chunkIdsToRemove.size,
        duration: manifestProcessingEnd - manifestProcessingStart,
        skippedForFeed: isFeedLocation,
      });

      const cleanupStart = performance.now();

      const audiobookPurgeStart = performance.now();
      // Explanation: getItemsByIds returns { [id: string]: TItem } where TItem is DocumentContent;
      // the cast adds `| undefined` to accurately model potential cache misses.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const cachedContent = await this.getItemsByIds(parsedDocIds) as { [parsedDocId: string]: DocumentContent | undefined; };
      const audiobookPurgeResult = await purgeAudiobooksForDocuments(
        docsInLocation,
        location,
        cachedContent,
        isFeedLocation,
      );
      const audiobookPurgeEnd = performance.now();

      const removeParsedStart = performance.now();
      await this.removeByIds(parsedDocIds);
      const removeParsedEnd = performance.now();
      this._documentLogger.debug('purgeLocation: removeByIds completed', {
        location,
        parsedDocCount: parsedDocIds.length,
        duration: removeParsedEnd - removeParsedStart,
      });

      const removeQueuedStart = performance.now();
      if (!isFeedLocation) {
        await documentChunks.removeQueuedManifestsForDocIds(docIds);
      }
      const removeQueuedEnd = performance.now();
      this._documentLogger.debug('purgeLocation: removeQueuedManifestsForDocIds completed', {
        location,
        docCount: isFeedLocation ? 0 : docIds.length,
        duration: removeQueuedEnd - removeQueuedStart,
        skippedForFeed: isFeedLocation,
      });

      const removeChunksStart = performance.now();
      if (!isFeedLocation && chunkIdsToRemove.size > 0) {
        await documentChunks.removeChunksByIds(Array.from(chunkIdsToRemove));
      }
      const removeChunksEnd = performance.now();

      this._documentLogger.debug('purgeLocation: removeChunksByIds completed', {
        location,
        chunkCount: chunkIdsToRemove.size,
        audiobookTrackCount: audiobookPurgeResult.removedAudiobookTrackIds.length,
        duration: removeChunksEnd - removeChunksStart,
        skippedForFeed: isFeedLocation,
      });

      const cleanupEnd = performance.now();
      this._documentLogger.debug('purgeLocation: sequential cleanup completed', {
        location,
        totalDuration: cleanupEnd - cleanupStart,
        steps: {
          audiobookPurge: audiobookPurgeEnd - audiobookPurgeStart,
          removeParsed: removeParsedEnd - removeParsedStart,
          removeQueued: removeQueuedEnd - removeQueuedStart,
          removeChunks: removeChunksEnd - removeChunksStart,
        },
      });

      const result = {
        removedParsedDocIds: parsedDocIds,
        removedChunkIds: Array.from(chunkIdsToRemove),
        removedAudiobookTrackIds: audiobookPurgeResult.removedAudiobookTrackIds,
        removedQueuedDocIds: docIds,
      };

      const totalTime = performance.now() - startTime;
      this._documentLogger.debug('purgeLocation completed', {
        location,
        counts: {
          parsed: result.removedParsedDocIds.length,
          chunks: result.removedChunkIds.length,
          audiobookTracks: result.removedAudiobookTrackIds.length,
          queued: result.removedQueuedDocIds.length,
        },
        totalDuration: totalTime,
        breakdown: {
          clearSyncState: clearSyncStateEnd - clearSyncStateStart,
          dbQuery: dbQueryEnd - dbQueryStart,
          docProcessing: docProcessingEnd - docProcessingStart,
          manifestProcessing: manifestProcessingEnd - manifestProcessingStart,
          cleanup: cleanupEnd - cleanupStart,
          cleanupSteps: {
            removeParsed: removeParsedEnd - removeParsedStart,
            removeQueued: removeQueuedEnd - removeQueuedStart,
            removeChunks: removeChunksEnd - removeChunksStart,
            audiobookPurge: audiobookPurgeEnd - audiobookPurgeStart,
          },
        },
      });

      this.emit('purge-completed', { location });

      return result;
    } catch (e) {
      const totalTime = performance.now() - startTime;
      const errorMessage = e instanceof Error ? e.message : String(e);
      this._documentLogger.error('purgeLocation failed', {
        location,
        errorMessage,
        e,
        totalDuration: totalTime,
      });
      this.emit('purge-completed', { location });
      throw e;
    }
  }

  private getDisabledLocations(
    locationPrefs: { [key in CacheDocumentLocation]?: boolean },
  ): CacheDocumentLocation[] {
    const disabledLocations: CacheDocumentLocation[] = [];

    const allLocations: CacheDocumentLocation[] = [
      DocumentLocation.New,
      DocumentLocation.Later,
      DocumentLocation.Shortlist,
      DocumentLocation.Archive,
      DocumentLocation.Feed,
    ];

    for (const location of allLocations) {
      if (locationPrefs[location] === false) {
        disabledLocations.push(location);
      }
      if (location === DocumentLocation.Feed && locationPrefs[location] === undefined) {
        disabledLocations.push(location);
      }
    }

    return disabledLocations;
  }

  private async resumeLocationIfNeeded(location: CacheDocumentLocation): Promise<void> {
    if (this._ongoingLocationPromises.has(location)) {
      this._documentLogger.debug('Location already loading, skipping resume', { location });
      return;
    }

    // Check if location is currently enabled before resuming
    // We might have disabled this location in between running other locations,
    // so we should check again here.
    const offlinePrefs = await foreground.getOfflineCacheEnabledByLocation();
    if (!offlinePrefs[location]) {
      this._documentLogger.debug('Skipping resume for disabled location', { location });
      // NOTE: We don't clear sync state for disabled locations to preserve the last sync timestamp.
      // This allows for efficient incremental syncing when the location is re-enabled, avoiding
      // unnecessary re-downloads of content we already have cached.
      // await this.clearLocationSyncState(location);
      return;
    }

    this._documentLogger.debug('resumeLocationIfNeeded: processing location', { location });
    try {
      await this.loadForLocation(location);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('aborted') || e instanceof Error && e.name === 'AbortError') {
        this._documentLogger.debug('resumeLocationIfNeeded cancelled', { location });
      } else {
        this._documentLogger.error('resumeLocationIfNeeded failed', { location, errorMessage, e });
        throw e;
      }
    }
  }

  private async updateForegroundDownloadingLocation() {
    const operations = this.getOngoingOperations();

    for (const location of PRIORITY_ORDERED_LOCATIONS) {
      if (operations[location]) {
        const isInitial = await this.isLocationSyncInitial(location);
        if (isInitial) {
          await foreground.setCurrentDownloadingLocation(location);
          return;
        }
      }
    }

    await foreground.setCurrentDownloadingLocation(null);
  }

  private async loadLocationWithCursor(location: CacheDocumentLocation, signal?: AbortSignal): Promise<void> {
    let updatedSince: number | undefined;
    let cursorToStartAt: string | undefined;

    const ongoingLocationFullSync = await this._cache.getItem<OngoingLocationFullSync>(
      `ongoingLocationFullSync_${location}`,
    );

    if (ongoingLocationFullSync) {
      cursorToStartAt = ongoingLocationFullSync.nextCursor;
      this._documentLogger.debug('Continuing incomplete full sync', {
        location,
        cursor: cursorToStartAt,
      });
    } else {
      const lastLocationSync = await this._cache.getItem<number>(`locationLastSync_${location}`);
      if (lastLocationSync) {
        updatedSince = lastLocationSync;

        const ongoingUpdatesCursor = await this._cache.getItem<string>(
          `ongoingLocationUpdatesCursor_${location}`,
        );
        if (ongoingUpdatesCursor) {
          cursorToStartAt = ongoingUpdatesCursor;
          this._documentLogger.debug('Continuing incomplete update sync', {
            location,
            updatedSince,
            cursor: cursorToStartAt,
          });
        } else {
          this._documentLogger.debug('Starting fresh update sync', {
            location,
            updatedSince,
          });
        }
      } else {
        this._documentLogger.debug('Starting first full sync', { location });
      }
    }

    const thisRequestStartDatetime = nowTimestamp();

    const extraQueryParams = new URLSearchParams();
    extraQueryParams.append('filter[triage_status][in]', location);

    await this._itemFetcher.fetchItemsByCursor({
      cursorToStartAt,
      updatedSince,
      signal,
      extraQueryParams,
      onItemPageReceived: async (items, requestedIds, nextCursor) => {
        if (signal?.aborted) {
          this._documentLogger.debug('loadLocationWithCursor: skipping page processing due to abort signal', {
            location,
            itemsLength: items.length,
          });
          return;
        }


        this._documentLogger.debug('loadLocationWithCursor: page received', {
          location,
          totalItems: items.length,
          nextCursor: nextCursor ? 'present' : 'none',
        });

        if (items.length > 0 && !signal?.aborted) {
          await this.upsertIntoCache(requestedIds, items, signal);
        }

        if (ongoingLocationFullSync) {
          if (nextCursor) {
            await this._cache.setItem(`ongoingLocationFullSync_${location}`, {
              nextCursor,
              requestStartDatetime: thisRequestStartDatetime,
            });
          } else if (!signal?.aborted) {
            await this._cache.removeItem(`ongoingLocationFullSync_${location}`);
            await this._cache.setItem(`locationLastSync_${location}`, thisRequestStartDatetime);
            this._documentLogger.info('Completed full sync for location', { location });
          }
        } else if (updatedSince) {
          if (nextCursor) {
            await this._cache.setItem(`ongoingLocationUpdatesCursor_${location}`, nextCursor);
          } else if (!signal?.aborted) {
            await this._cache.removeItem(`ongoingLocationUpdatesCursor_${location}`);
            await this._cache.setItem(`locationLastSync_${location}`, thisRequestStartDatetime);
            this._documentLogger.info('Completed update sync for location', { location, updatedSince });
          }
        } else if (nextCursor) {
          await this._cache.setItem(`ongoingLocationFullSync_${location}`, {
            nextCursor,
            requestStartDatetime: thisRequestStartDatetime,
          });
        } else if (!signal?.aborted) {
          await this._cache.setItem(`locationLastSync_${location}`, thisRequestStartDatetime);
          this._documentLogger.info('Completed first sync for location', { location });
        }
      },
    });
  }
}

export default DocumentContentStore;
